import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { PlayIcon, CloseIcon, VolumeIcon, ArrowIcon, FeelIcon, UsersIcon, SparkleIcon } from './BespokeIcons';

/**
 * PurposeIntro - A cinematic, filmic experience explaining the purpose of ElchmE
 * 
 * Key message: It's not about winning, being nice, or proving you're right.
 * It's about finding a resolution where EVERYONE walks away with a win.
 */
const PurposeIntro = ({ onComplete, onSkip }) => {
  const [currentScene, setCurrentScene] = useState(0);
  const [textVisible, setTextVisible] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [showSkip, setShowSkip] = useState(false);
  const audioRef = useRef(null);

  // Show skip after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowSkip(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Scene progression
  useEffect(() => {
    // Fade in text after scene loads
    const fadeTimer = setTimeout(() => setTextVisible(true), 500);
    return () => clearTimeout(fadeTimer);
  }, [currentScene]);

  // Auto-advance scenes
  useEffect(() => {
    if (currentScene >= scenes.length) {
      onComplete();
      return;
    }
    
    const duration = scenes[currentScene]?.duration || 5000;
    const timer = setTimeout(() => {
      setTextVisible(false);
      setTimeout(() => setCurrentScene(prev => prev + 1), 800);
    }, duration);
    
    return () => clearTimeout(timer);
  }, [currentScene, onComplete]);

  // Ambient audio
  useEffect(() => {
    if (!audioEnabled) return;
    
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioContext();
    
    // Create ethereal ambient sound
    const createAmbient = () => {
      const osc1 = audioCtx.createOscillator();
      const osc2 = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      const filter = audioCtx.createBiquadFilter();
      
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(110, audioCtx.currentTime);
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(165, audioCtx.currentTime);
      
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(400, audioCtx.currentTime);
      
      gain.gain.setValueAtTime(0, audioCtx.currentTime);
      gain.gain.linearRampToValueAtTime(0.05, audioCtx.currentTime + 2);
      
      osc1.connect(filter);
      osc2.connect(filter);
      filter.connect(gain);
      gain.connect(audioCtx.destination);
      
      osc1.start();
      osc2.start();
      
      return { osc1, osc2, gain, cleanup: () => {
        gain.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 1);
        setTimeout(() => {
          osc1.stop();
          osc2.stop();
        }, 1000);
      }};
    };

    const ambient = createAmbient();
    audioRef.current = { audioCtx, ambient };

    return () => {
      ambient.cleanup();
      audioCtx.close();
    };
  }, [audioEnabled]);

  const scenes = [
    {
      id: 'opening',
      duration: 5000,
      background: 'radial-gradient(ellipse at center, rgba(20, 15, 40, 1) 0%, rgba(8, 10, 14, 1) 100%)',
      content: (
        <div className="text-center max-w-3xl mx-auto px-6">
          <p className="text-white/40 text-sm uppercase tracking-[0.3em] mb-8 animate-fade-in-slow">
            Before we begin
          </p>
          <h1 
            className="text-3xl md:text-4xl lg:text-5xl font-light text-white/90 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            When we're activated, we don't just <span className="text-amber-400">"disagree"</span>
          </h1>
        </div>
      )
    },
    {
      id: 'insight-1',
      duration: 5000,
      background: 'radial-gradient(ellipse at 30% 50%, rgba(40, 20, 20, 0.8) 0%, rgba(8, 10, 14, 1) 70%)',
      content: (
        <div className="text-center max-w-3xl mx-auto px-6">
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-light text-white/80 leading-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            We lose access to our <span className="text-red-400">best thinking</span>
          </h2>
          <p className="text-white/40 text-lg max-w-xl mx-auto">
            Our reactive mind takes over, clouding clarity
          </p>
        </div>
      )
    },
    {
      id: 'insight-2',
      duration: 5500,
      background: 'radial-gradient(ellipse at center, rgba(30, 25, 15, 0.9) 0%, rgba(8, 10, 14, 1) 70%)',
      content: (
        <div className="text-center max-w-4xl mx-auto px-6">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <SparkleIcon size={32} color="#FBBF24" />
          </div>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-light text-white/90 leading-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            ElchmE helps you return to a <span className="text-amber-400">calm, potent</span> state
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto">
            So you can hear what's actually happening—in you, and in them
          </p>
        </div>
      )
    },
    {
      id: 'insight-3',
      duration: 5000,
      background: 'radial-gradient(ellipse at 70% 50%, rgba(20, 40, 30, 0.8) 0%, rgba(8, 10, 14, 1) 70%)',
      content: (
        <div className="text-center max-w-4xl mx-auto px-6">
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-light text-white/90 leading-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            From there, the <span className="text-emerald-400">hidden options</span> appear
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto">
            Solutions that were previously invisible become clear
          </p>
        </div>
      )
    },
    {
      id: 'benefits',
      duration: 5500,
      background: 'radial-gradient(ellipse at 40% 60%, rgba(30, 20, 40, 0.9) 0%, rgba(8, 10, 14, 1) 70%)',
      content: (
        <div className="text-center max-w-4xl mx-auto px-6">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-rose-500/10 border border-rose-500/30 flex items-center justify-center">
            <FeelIcon size={32} color="#FB7185" />
          </div>
          <div className="space-y-4">
            <p className="text-xl md:text-2xl text-red-400/60 line-through decoration-1">
              Less suffering. Less lingering resentment.
            </p>
            <p className="text-2xl md:text-3xl text-emerald-400 font-light">
              More fulfillment. More trust. Better results.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'closing',
      duration: 5000,
      background: 'radial-gradient(ellipse at center, rgba(50, 40, 20, 0.6) 0%, rgba(8, 10, 14, 1) 60%)',
      content: (
        <div className="text-center max-w-4xl mx-auto px-6">
          <blockquote 
            className="text-2xl md:text-3xl lg:text-4xl font-light text-amber-200/80 leading-relaxed mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            "Before Building, Build <span className="text-amber-400">Belonging</span>..."
          </blockquote>
          <p className="text-white/50 text-xl">that's ElchmE!</p>
        </div>
      )
    },
    {
      id: 'ready',
      duration: 4000,
      background: 'radial-gradient(ellipse at center, rgba(60, 50, 25, 0.7) 0%, rgba(8, 10, 14, 1) 60%)',
      content: (
        <div className="text-center max-w-3xl mx-auto px-6">
          <div className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-br from-amber-400/30 to-amber-600/20 border border-amber-500/40 flex items-center justify-center animate-pulse-slow">
            <SparkleIcon size={48} color="#FBBF24" />
          </div>
          <h2 
            className="text-3xl md:text-4xl font-light text-white/90 mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Are you ready?
          </h2>
          <Button
            onClick={onComplete}
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-medium px-10 py-6 text-lg rounded-lg shadow-lg shadow-amber-500/20"
            data-testid="purpose-intro-continue-btn"
          >
            <PlayIcon size={20} color="#000000" className="mr-2" />
            Enter the Field
          </Button>
        </div>
      )
    }
  ];

  const currentSceneData = scenes[currentScene];
  if (!currentSceneData) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden transition-all duration-1000"
      style={{ background: currentSceneData.background }}
      data-testid="purpose-intro"
    >
      {/* Ambient particle effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-amber-400/10"
            style={{
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${10 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      {/* Cinematic letterbox bars */}
      <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-black to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black to-transparent pointer-events-none" />

      {/* Content */}
      <div 
        className={`relative z-10 transition-all duration-700 ${
          textVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        {currentSceneData.content}
      </div>

      {/* Progress indicator */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-2">
        {scenes.map((_, idx) => (
          <div
            key={idx}
            className={`h-1 rounded-full transition-all duration-500 ${
              idx < currentScene ? 'w-3 bg-amber-500/60' :
              idx === currentScene ? 'w-8 bg-amber-400' :
              'w-3 bg-white/20'
            }`}
          />
        ))}
      </div>

      {/* Audio toggle */}
      <button
        onClick={() => setAudioEnabled(!audioEnabled)}
        className="absolute top-6 right-6 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        aria-label={audioEnabled ? "Mute audio" : "Enable audio"}
      >
        {audioEnabled ? (
          <VolumeIcon size={20} color="rgba(255,255,255,0.6)" />
        ) : (
          <VolumeIcon size={20} color="rgba(255,255,255,0.4)" muted={true} />
        )}
      </button>

      {/* Skip button */}
      {showSkip && currentScene < scenes.length - 1 && (
        <button
          onClick={onSkip || onComplete}
          className="absolute right-4 inline-flex items-center gap-2 min-h-[44px] px-4 py-2 rounded-full border border-white/15 bg-white/5 text-white/70 hover:text-white hover:bg-white/10 hover:border-white/30 backdrop-blur-md transition-[background-color,border-color,color] duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)' }}
          data-testid="purpose-intro-skip-btn"
        >
          Skip intro
          <ArrowIcon size={16} color="currentColor" direction="right" />
        </button>
      )}

      {/* Scene counter */}
      <div className="absolute top-6 left-6 text-white/20 text-xs font-mono">
        {currentScene + 1} / {scenes.length}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.3; }
          25% { transform: translateY(-20px) translateX(10px); opacity: 0.6; }
          50% { transform: translateY(-10px) translateX(-5px); opacity: 0.4; }
          75% { transform: translateY(-30px) translateX(5px); opacity: 0.5; }
        }
        @keyframes fade-in-slow {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-slow {
          animation: fade-in-slow 1.5s ease-out forwards;
        }
        @keyframes pulse-slow {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.05); opacity: 1; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default PurposeIntro;
